import {
  action,
  atom,
  batch,
  onConnect,
  reaction,
  withAssign,
} from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'

import { reatomAsyncState } from './reatom-async-state'

import type { Ctx, CtxSpy } from '@reatom/framework'

export interface PaginatedOptions<E, A> {
  name: string
  limit?: number
  loadItems: (
    ctx: Ctx | CtxSpy,
    params: {
      limit: number
      offset: number
    },
  ) => Promise<Result.Result<Array<A>, E>>
  strategy?: 'reload-on-each-connect' | 'load-once'
}

export const reatomPaginated = <E, A>({
  name,
  limit = 20,
  loadItems,
  strategy = 'load-once',
}: PaginatedOptions<E, A>) => {
  const offsetAtom = atom(0, `${name}.offsetAtom`)
  const hasMoreItemsAtom = atom(true, `${name}.hasMoreItemsAtom`)
  const loadMoreStateAtom = reatomAsyncState.notInitiated<E, Array<A>>()
  const itemsStateAtom = reatomAsyncState.notInitiated<E, Array<A>>()

  const loadItemsAction = reaction(async (ctx) => {
    itemsStateAtom.setLoading(ctx)
    offsetAtom(ctx, 0)
    hasMoreItemsAtom(ctx, true)

    // eslint-disable-next-line @reatom/async-rule
    const result = await loadItems(ctx, {
      limit,
      offset: 0,
    })

    batch(ctx, () => {
      itemsStateAtom.setResult(ctx, result)
      if (Result.isSuccess(result)) {
        hasMoreItemsAtom(ctx, result.success.length >= limit)
      }
    })
  }, `${name}.loadItemsAction`)

  const loadMoreItemsAction = action(async (ctx) => {
    const state = ctx.get(itemsStateAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }

    const currentOffset = ctx.get(offsetAtom)
    const nextOffset = currentOffset + limit

    loadMoreStateAtom.setLoading(ctx)
    const result = await ctx.schedule(() =>
      loadItems(ctx, {
        limit,
        offset: nextOffset,
      }),
    )

    batch(ctx, () => {
      loadMoreStateAtom.setResult(ctx, result)
      if (Result.isSuccess(result)) {
        offsetAtom(ctx, nextOffset)
        hasMoreItemsAtom(ctx, result.success.length >= limit)

        const itemsState = ctx.get(itemsStateAtom)
        if (AsyncStateN.isSuccess(itemsState)) {
          const updatedItems = [...itemsState.value, ...result.success]
          itemsStateAtom.setSuccess(ctx, updatedItems)
        }
      }
    })
  }, `${name}.loadMoreItemsAction`)

  const countAtom = atom((ctx) => {
    const state = ctx.spy(itemsStateAtom)
    return AsyncStateN.isSuccess(state) ? state.value.length : 0
  }, `${name}.countAtom`)

  onConnect(itemsStateAtom, (ctx) => {
    const state = ctx.get(itemsStateAtom)
    switch (strategy) {
      case 'reload-on-each-connect': {
        break
      }
      case 'load-once': {
        {
          if (AsyncStateN.isSuccess(state)) {
            return
          }
        }
      }
    }
    const sub = loadItemsAction(ctx)
    return () => sub.unsubscribe()
  })

  const refreshAction = action(async (ctx) => {
    const result = await ctx.schedule(() =>
      loadItems(ctx, {
        limit,
        offset: 0,
      }),
    )

    batch(ctx, () => {
      itemsStateAtom.setResult(ctx, result)
      if (Result.isSuccess(result)) {
        offsetAtom(ctx, 0)
        hasMoreItemsAtom(ctx, result.success.length >= limit)
      }
    })
  }, `${name}.refreshAction`)

  return itemsStateAtom.pipe(
    withAssign(() => ({
      load: loadItemsAction,
      loadMore: loadMoreItemsAction,
      refresh: refreshAction,
      hasMoreItems: hasMoreItemsAtom,
      loadMoreState: loadMoreStateAtom,
      count: countAtom,
    })),
  )
}
